import React from 'react';
import { StyleProp, StyleSheet, type TextStyle, View, type ViewStyle } from 'react-native';

import { theme } from '@garnish/constants';
import { useResponsive } from '@garnish/core';

import { useA11ySelector } from '@sg/a11y-helpers';

import { LoyaltyHomeProgressBarReward } from './LoyaltyHomeProgressBarReward';

export const LoyaltyHomeProgressBarMilestone = (props: LoyaltyHomeProgressBarMilestoneProps) => {
  const {
    availablePoints,
    pointsReward,
    palette,
    size,
    containerStyle,
    milestoneStyle,
    textStyle,
    isFluid = true,
    index,
  } = props;

  const { props: a11yProps } = useA11ySelector('loyalty.rewards-bar.item', { index }); // prettier-ignore
  const { match } = useResponsive();

  const containerStyles = [
    match([styles.containerXS, styles.containerSM]),
    isFluid ? styles.containerFluid : styles.containerCompact,
  ];
  const milestoneStyles = match([styles.milestoneXS, styles.milestoneSM]);

  const points = pointsReward?.points ?? 0;
  const hasReachedMilestone = availablePoints > 0 && availablePoints >= (points ?? 0);

  const paletteStyle = milestonePalette[palette];
  const reachedStyle = hasReachedMilestone ? milestoneReachedPalette[palette] : null;

  return (
    <View style={[containerStyles, containerStyle]} {...a11yProps}>
      <View style={[milestoneStyles, milestoneStyle, paletteStyle, reachedStyle]} />

      <LoyaltyHomeProgressBarReward
        points={pointsReward?.points ?? 0}
        assetUrl={pointsReward?.assetUrl}
        name={pointsReward?.displayName ?? pointsReward?.name}
        textStyle={[textPalette[palette], textStyle]}
        hasReachedMilestone={hasReachedMilestone}
        size={size}
        align={isFluid ? 'center' : 'start'}
      />
    </View>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    marginTop: -theme.spacing['3'],
  },
  containerSM: {
    marginTop: -theme.spacing['6'],
  },
  containerFluid: {
    flex: 1,
    alignItems: 'center',
    gap: theme.spacing['6'],
  },
  containerCompact: {
    flex: 0.5,
    alignItems: 'flex-start',
    gap: theme.spacing['6'],
    paddingHorizontal: theme.spacing['3'],
  },
  milestoneXS: {
    width: 4,
    height: 4,
    borderRadius: 4,
    marginTop: 2,
    marginBottom: -theme.spacing['3'],
  },
  milestoneSM: {
    width: 10,
    height: 10,
    borderRadius: 10,
  },
  darkMilestone: {
    backgroundColor: theme.colors.KALE,
  },
  lightMilestone: {
    backgroundColor: theme.colors.OPACITY.KALE.LIGHT,
  },
  darkMilestoneReached: {
    backgroundColor: theme.colors.KALE,
  },
  lightMilestoneReached: {
    backgroundColor: theme.colors.WHITE,
  },
  darkText: {
    color: theme.colors.CREAM,
  },
  lightText: {
    color: theme.colors.TEXT_COLOR,
  },
  whiteMilestone: {
    backgroundColor: theme.colors.WHITE,
  },
  whiteMilestoneReached: {
    backgroundColor: theme.colors.WHITE,
  },
  whiteText: {
    color: theme.colors.WHITE,
  },
});

// ─── Palettes ───────────────────────────────────────────────────────────────

const milestonePalette: Record<LoyaltyHomeProgressBarMilestoneProps['palette'], ViewStyle> = {
  dark: styles.darkMilestone,
  light: styles.lightMilestone,
  white: styles.whiteMilestone,
};

const milestoneReachedPalette: Record<LoyaltyHomeProgressBarMilestoneProps['palette'], ViewStyle> =
  {
    dark: styles.darkMilestoneReached,
    light: styles.lightMilestoneReached,
    white: styles.whiteMilestoneReached,
  };

const textPalette: Record<LoyaltyHomeProgressBarMilestoneProps['palette'], TextStyle> = {
  dark: styles.darkText,
  light: styles.lightText,
  white: styles.whiteText,
};

// ─── Types ──────────────────────────────────────────────────────────────────

type LoyaltyHomeProgressBarMilestoneProps = {
  availablePoints: number;
  pointsReward?: PointsReward;
  palette: 'dark' | 'light' | 'white';
  size?: 'medium' | 'large';
  containerStyle?: StyleProp<ViewStyle>;
  milestoneStyle?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  isFluid?: boolean;
  index?: number;
};

type PointsReward = {
  name?: string;
  displayName?: string;
  points?: number | null;
  assetUrl?: string | null;
};
